import { useMemo } from 'react'
import type {
  HazardIdentification,
  MachineLocation,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useMachineryDirectiveEhsrList } from '../../hooks/useMachineryDirectiveEhsrList'
import {
  ChevronsRight,
  MoreHorizontal,
  RefreshCcw,
  MapPinIcon,
  ShieldCheck,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { EhsrBadge } from '../../ehsr/EhsrBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import { getHazardMachineLocationLabel } from './getHazardMachineLocationLabel'
import { HazardRiskScore } from './HazardRiskScore'
import { RiskInFactorsGrid } from './RiskInFactorsGrid'
import { getIso7010PictogramUrl } from './iso7010Pictograms'

export function HazardCard({
  hazard,
  machineLocationsById,
  onEdit,
  onDelete,
  isMutationPending,
  showHeader = false,
}: {
  hazard: HazardIdentification
  machineLocationsById: ReadonlyMap<string, MachineLocation>
  onEdit: (hazard: HazardIdentification) => void
  onDelete: (hazardId: string) => void
  isMutationPending: boolean
  showHeader?: boolean
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const ehsrOptions = useMachineryDirectiveEhsrList()
  const naLabel = t('hazardsPage.hazardDetail.na')
  const locationLabel = getHazardMachineLocationLabel(
    hazard,
    machineLocationsById,
    t
  )
  const displayText =
    hazard.description?.trim() ||
    hazard.type ||
    locationLabel ||
    t('hazardsPage.noDescription')

  const clauseById = useMemo(
    () => new Map(ehsrOptions.map((opt) => [opt.id, opt])),
    [ehsrOptions]
  )
  const ehsrIds = hazard.ehsrClauseIds ?? []

  return (
    <Card>
      {showHeader && (
        <CardHeader>
          <div className='flex flex-row items-center justify-between'>
            <CardTitle>
              {hazard.type
                ? t(`hazardsPage.enums.hazardTypes.${hazard.type}`)
                : naLabel}{' '}
              (#{hazard.id})
            </CardTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='size-8'
                  disabled={isMutationPending}
                >
                  <MoreHorizontal className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem asChild>
                  <Link to={hazard.id}>{t('hazardsPage.view')}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit(hazard)}>
                  {t('hazardsPage.hazardDetail.edit')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onDelete(hazard.id)}
                  className='text-destructive focus:text-destructive'
                >
                  {t('hazardsPage.hazardDetail.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
      )}
      <CardContent className='space-y-2 pt-4'>
        <div className='flex items-start gap-2'>
          {hazard.pictogramId != null && hazard.pictogramId.trim() !== '' ? (
            <img
              src={getIso7010PictogramUrl(hazard.pictogramId)}
              alt=''
              className='size-8 shrink-0 object-contain'
            />
          ) : null}
          <p className='min-w-0 flex-1 text-sm text-muted-foreground'>
            {displayText}
          </p>
        </div>
        <Item variant='outline' size='xs'>
          <ItemMedia variant='icon'>
            <MapPinIcon />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>{t('hazardsPage.locationLabel')}</ItemTitle>
            <ItemDescription>{locationLabel ?? '—'}</ItemDescription>
          </ItemContent>
        </Item>
        <Item variant='outline' size='xs'>
          <ItemMedia variant='icon'>
            <RefreshCcw />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>{t('hazardsPage.lifecyclesLabel')}</ItemTitle>
            <ItemDescription>
              {hazard.lifecycles
                .map((lifecycle) =>
                  t(`hazardsPage.enums.hazardLifecycles.${lifecycle}`)
                )
                .join(', ')}
            </ItemDescription>
          </ItemContent>
        </Item>
        {ehsrIds.length > 0 && (
          <Item variant='outline' size='xs'>
            <ItemMedia variant='icon'>
              <ShieldCheck />
            </ItemMedia>
            <ItemContent>
              <ItemTitle>{t('hazardsPage.hazardDetail.ehsr')}</ItemTitle>
              <ItemDescription>
                <div className='flex flex-wrap gap-1 pt-0.5'>
                  {ehsrIds.map((clauseId) => (
                    <EhsrBadge
                      key={clauseId}
                      clauseId={clauseId}
                      clause={clauseById.get(clauseId)}
                    />
                  ))}
                </div>
              </ItemDescription>
            </ItemContent>
          </Item>
        )}
        <div className='grid grid-cols-1 gap-2'>
          <RiskInFactorsGrid
            injury={hazard.riskInInjury}
            frequency={hazard.riskInFrequency}
            likelihood={hazard.riskInLikelihood}
            avoidance={hazard.riskInAvoidance}
          />

          <Item variant='outline'>
            <ItemContent>
              <ItemTitle></ItemTitle>
              <ItemDescription>
                <div className='flex items-center justify-center gap-4 text-lg'>
                  <div>
                    <HazardRiskScore score={hazard.riskInScore} />
                    <span className='text-sm text-muted-foreground'>
                      {t('hazardsPage.hazardDetail.riskInScore')}
                    </span>
                  </div>
                  {showHeader && (
                    <>
                      <div className='flex grow-0 items-center gap-2'>
                        <ChevronsRight className='size-4' />
                        <Link
                          to={hazard.id}
                          className='text-sm break-all text-muted-foreground'
                        >
                          <Button variant='link' size='sm'>
                            {' '}
                            {t('hazardsPage.riskAssessmentMeasuresCount', {
                              count: hazard.hazardRiskReductionStepsCount ?? 0,
                            })}{' '}
                          </Button>
                        </Link>
                      </div>
                      <div>
                        <HazardRiskScore
                          score={hazard.finalRiskOutScore ?? hazard.riskInScore}
                        />
                        <span className='text-sm text-muted-foreground'>
                          {t('hazardsPage.finalRiskOutScoreLabel')}{' '}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </ItemDescription>
            </ItemContent>
          </Item>
        </div>
      </CardContent>
    </Card>
  )
}
